<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import AutoResizeTextarea from '@/components/common/atoms/auto-resize-textarea.vue';
import Popup from '@/components/theme/edit/theme-setting-support-modal/popup.vue';
import QueryEditor from '@/components/theme/edit/theme-setting-support-modal/query-editor.vue';
import ThemePreviewInThemeSettingSupportModal from '@/components/theme/edit/theme-setting-support-modal/theme-preview-in-theme-setting-support-modal.vue';
import { useThemeSettingSupportModal } from '@/components/theme/edit/theme-setting-support-modal/useThemeSettingSupportModal';
import {
  OpenPopupParam,
  ThemeQueryGenerationRequest,
} from '@/types/themeQueryGenerationRequest';
import { useEnterKeyHandler } from '@/utils/composables/useEnterKeyHandler';
import { useEmitter } from '@/utils/vue';

export interface Props {
  themeQueryGenerationRequest: ThemeQueryGenerationRequest;
}

const props = defineProps<Props>();
const showEnglishQuery = ref<boolean>(true);
const isEditingRequestText = ref<boolean>(false);
const route = useRoute();
const themeId = ref(Number(route.params.themeId ?? 0));
const enterRequestTextEditMode = () => {
  isEditingRequestText.value = true;
};
const requestText = ref<string>('');

// 各行の前後の空白を削除する
const trimmedInputText = computed(() =>
  requestText.value
    .split('\n')
    .map(line => line.trim())
    .join('\n'),
);

const enableSubmitButton = computed(() => {
  return trimmedInputText.value.length > 0;
});

const emitter = useEmitter();
const {
  generateQueryFromKeywords,
  generateQueryFromExcludedKeywords,
  createDeletedKeywordUuid,
  filterKeywordDatasetByDeletedKeywordsAndDuplications,
  filterExcludedKeywordsByDeletedKeywords,
} = useThemeSettingSupportModal();

/**
 * 削除されたキーワードのUUIDのSet
 */
const deletedKeywordUuidsSet = computed(
  () =>
    new Set(
      props.themeQueryGenerationRequest.deleted_keywords.map(deletedKeyword =>
        createDeletedKeywordUuid(deletedKeyword.uuid, deletedKeyword.lang),
      ),
    ),
);

/**
 * 削除されたキーワードと重複を除外した後のキーワードデータ (日本語クエリ用)
 */
const filteredKeywordsJa = computed(() =>
  filterKeywordDatasetByDeletedKeywordsAndDuplications({
    keywordDataset: props.themeQueryGenerationRequest.generated_query.keywords,
    deletedKeywordUuidsSet: deletedKeywordUuidsSet.value,
    lang: 'ja',
  }),
);

/**
 * 削除されたキーワードと重複を除外した後のキーワードデータ (英語クエリ用)
 */
const filteredKeywordsEn = computed(() =>
  filterKeywordDatasetByDeletedKeywordsAndDuplications({
    keywordDataset: props.themeQueryGenerationRequest.generated_query.keywords,
    deletedKeywordUuidsSet: deletedKeywordUuidsSet.value,
    lang: 'en',
  }),
);

/**
 * 削除されたキーワードを除外した後の除外対象キーワードデータ (日本語クエリ用)
 */
const filteredExcludedKeywordsJa = computed(() =>
  filterExcludedKeywordsByDeletedKeywords({
    excludedKeywords:
      props.themeQueryGenerationRequest.generated_query.excluded_keywords,
    deletedKeywordUuidsSet: deletedKeywordUuidsSet.value,
    lang: 'ja',
  }),
);

/**
 * 削除されたキーワードを除外した後の除外対象キーワードデータ (英語クエリ用)
 */
const filteredExcludedKeywordsEn = computed(() =>
  filterExcludedKeywordsByDeletedKeywords({
    excludedKeywords:
      props.themeQueryGenerationRequest.generated_query.excluded_keywords,
    deletedKeywordUuidsSet: deletedKeywordUuidsSet.value,
    lang: 'en',
  }),
);

const cancelRequestTextEdit = () => {
  requestText.value = props.themeQueryGenerationRequest.request_text;
  isEditingRequestText.value = false;
};
const saveRequestTextEdit = async () => {
  isEditingRequestText.value = false;
  if (
    trimmedInputText.value !== props.themeQueryGenerationRequest.request_text
  ) {
    emit('edit-request-text', trimmedInputText.value);
  } else {
    // trimming後の値に変更がない場合は元の値に戻す (前後に追加された空白が残ってしまうため)
    requestText.value = props.themeQueryGenerationRequest.request_text;
  }
};
const addQueries = async () => {
  emitter.emit('add-queries-from-theme-setting-support', keywords.value);
  await api.trackThemeEvent('add_keywords', {
    pageName: 'theme_setting',
    pageUrl: route.fullPath,
    ...(themeId.value ? { theme: { id: themeId.value } } : {}),
    from: 'ai_generator',
    prompt: props.themeQueryGenerationRequest.request_text,
    keywords: keywords.value,
  });
  emit('close-modal');
};
const deleteKeyword = (uuid: string, lang: 'ja' | 'en') => {
  emit('delete-keyword', uuid, lang);
  closePopup();
};
const restoreKeyword = (uuid: string, lang: 'ja' | 'en') => {
  emit('restore-keyword', uuid, lang);
  closePopup();
};

const { handleKeyDown, handleCompositionStart, handleCompositionEnd } =
  useEnterKeyHandler({ sendCallback: saveRequestTextEdit });

watch(
  () => props.themeQueryGenerationRequest.request_text,
  newRequestText => {
    requestText.value = newRequestText;
  },
  { immediate: true },
);

/**
 * 削除されたキーワードを除外した後のキーワードデータを基にクエリを生成する
 */
const keywords = computed(() => {
  const filteredKeywordQueryJa = generateQueryFromKeywords(
    filteredKeywordsJa.value,
    'ja',
  );
  const filteredKeywordQueryEn = showEnglishQuery.value
    ? generateQueryFromKeywords(filteredKeywordsEn.value, 'en')
    : '';
  const filteredExcludedKeywordsQueryJa = generateQueryFromExcludedKeywords(
    filteredExcludedKeywordsJa.value,
    'ja',
  );
  const filteredExcludedKeywordsQueryEn = generateQueryFromExcludedKeywords(
    filteredExcludedKeywordsEn.value,
    'en',
  );
  return [
    ...(filteredKeywordQueryJa
      ? [
          filteredKeywordQueryJa +
            (filteredExcludedKeywordsQueryJa
              ? ` ${filteredExcludedKeywordsQueryJa}`
              : ''),
        ]
      : []),
    ...(filteredKeywordQueryEn
      ? [
          filteredKeywordQueryEn +
            (filteredExcludedKeywordsQueryEn
              ? ` ${filteredExcludedKeywordsQueryEn}`
              : ''),
        ]
      : []),
  ];
});

// =========================
//  popup関連
// =========================
const selectedWord = ref<OpenPopupParam['word'] | null>(null);
const selectedLang = ref<'ja' | 'en'>('ja');
const isDeletedKeywordSelected = ref<boolean>(false);
const isPopupOpen = ref<boolean>(false);
const closePopup = () => {
  isPopupOpen.value = false;
};
const popup = ref<InstanceType<typeof Popup> | null>(null);
defineExpose({ popup, closePopup });
const emit = defineEmits<{
  (e: 'close-modal'): void;
  (e: 'edit-request-text', requestText: string): void;
  (e: 'delete-keyword', uuid: string, lang: 'ja' | 'en'): void;
  (e: 'restore-keyword', uuid: string, lang: 'ja' | 'en'): void;
}>();

/**
 * ポップアップがウィンドウの上下にはみ出さないように表示位置を設定する関数
 *
 * @param {HTMLElement} popupElem - ポップアップ要素
 * @param {DOMRect} popupRect - ポップアップ要素の矩形情報
 * @param {number} clientX - クライアントX座標
 * @param {DOMRect} targetElementRect - イベントが配信されたオブジェクトの矩形情報
 */
const setPopupPosition = (
  popupElem: HTMLElement,
  popupRect: DOMRect,
  clientX: number,
  targetElementRect: DOMRect,
) => {
  const heightBetweenWindowBottomAndPopup =
    window.innerHeight - targetElementRect.bottom;
  const heightUsedByPopup = popupRect.height;
  // ポップアップが下にはみ出す場合
  if (heightUsedByPopup >= heightBetweenWindowBottomAndPopup) {
    // NOTE: 24pxはウィンドウ下部とポップアップの間に余白を持たせるために設定
    popupElem.style.top = `${targetElementRect.bottom - (heightUsedByPopup - heightBetweenWindowBottomAndPopup + 24)}px`;
  } else {
    popupElem.style.top = `${targetElementRect.top}px`;
  }
  popupElem.style.left = `${clientX}px`;
};
const openPopup = ({ event, word, lang, isDeletedKeyword }: OpenPopupParam) => {
  isPopupOpen.value = true;
  selectedLang.value = lang;
  selectedWord.value = word;
  isDeletedKeywordSelected.value = isDeletedKeyword;
  // ポップアップメニューがDOMに追加された後に位置を調整
  nextTick(() => {
    // NOTE: @storybook/testの"userEvent"および"fireEvent"でイベントをトリガーすると"currentTarget"がnullになり
    // ポップアップを開けなかったため、"currentTarget"ではなく"target"を使用している。
    // イベントのトリガー要素は子要素を持たないため、"currentTarget"ではなく"target"を使っても問題ないと判断した。
    const targetElement = event.target as HTMLElement;
    const targetElementRect = targetElement.getBoundingClientRect();
    const clientX = event.clientX;
    const popupRect = popup.value?.popupRef?.getBoundingClientRect();
    if (popupRect && popup.value?.popupRef) {
      setPopupPosition(
        popup.value?.popupRef,
        popupRect,
        clientX,
        targetElementRect,
      );
    }
  });
};
</script>

<template>
  <div class="wrapper">
    <div v-if="isEditingRequestText" class="request-text-editor">
      <AutoResizeTextarea
        :send-callback="saveRequestTextEdit"
        :handlers="{
          keydown: handleKeyDown,
          compositionstart: handleCompositionStart,
          compositionend: handleCompositionEnd,
        }"
        :max-length="200"
        v-model="requestText"
      />
      <div class="controls">
        <button
          class="c-btn--small c-btn--auto cancel-btn"
          @click="cancelRequestTextEdit"
        >
          キャンセル
        </button>
        <button
          class="c-btn--small c-btn--auto c-btn--AnewsPrimary save-request-text-btn"
          :class="{
            disabled: !enableSubmitButton,
          }"
          :disabled="!enableSubmitButton"
          @click="saveRequestTextEdit"
        >
          保存
        </button>
      </div>
    </div>
    <div v-else class="request-text">
      {{ props.themeQueryGenerationRequest.request_text }}
      <div
        class="pencil-btn"
        role="button"
        aria-label="依頼を編集して再回答"
        @click="enterRequestTextEditMode"
      >
        <DgrIcon
          name="pencil"
          v-tooltip.bottom="{
            content: '依頼を編集して再回答',
            theme: 'menu',
          }"
        />
      </div>
    </div>
    <div class="content-wrapper">
      <QueryEditor
        @open-popup="openPopup"
        @add-queries="addQueries"
        v-model="showEnglishQuery"
        :keyword-dataset="
          props.themeQueryGenerationRequest.generated_query.keywords
        "
        :excluded-keywords="
          props.themeQueryGenerationRequest.generated_query.excluded_keywords
        "
        :filtered-keywords-ja="filteredKeywordsJa"
        :filtered-keywords-en="filteredKeywordsEn"
        :filtered-excluded-keywords-ja="filteredExcludedKeywordsJa"
        :filtered-excluded-keywords-en="filteredExcludedKeywordsEn"
        :deleted-keywords="props.themeQueryGenerationRequest.deleted_keywords"
        :explanation="
          props.themeQueryGenerationRequest.generated_query.explanation
        "
      />
      <ThemePreviewInThemeSettingSupportModal :keywords="keywords" />
    </div>
    <Popup
      v-if="isPopupOpen && selectedWord"
      :word="selectedWord"
      :lang="selectedLang"
      :is-deleted-keyword-selected="isDeletedKeywordSelected"
      ref="popup"
      @delete-keyword="deleteKeyword"
      @restore-keyword="restoreKeyword"
    />
  </div>
</template>

<style lang="scss" scoped>
.request-text-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
}
.cancel-btn {
  background: $color-gray600;
  color: #fff;
  border: 0px;
}
.request-text {
  font-size: 20px;
  margin-bottom: 32px;
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
  white-space: pre-wrap;
  line-height: 24px;
}
.pencil-btn {
  cursor: pointer;
}
.content-wrapper {
  display: flex;
  flex-direction: row;
  gap: 16px;
}
.section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.controls {
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
}
.save-request-text-btn {
  &.disabled {
    border: 0;
  }
}
</style>
